import React, { useState } from "react";
import { Filter, Download, Search, X, Plus, Eye, EyeOff } from "lucide-react";
import Button from "./Button";
import FilterToggleButton from "./FilterToggleButton";
import DateFilter from "./DateFilter";
import ViewToggle from "./ViewToggle";
import SearchOverlay from "./SearchOverlay";
const QUICK_DATE_PRESETS = [
  { id: "today", label: "T", title: "Today" },
  { id: "this_week", label: "W", title: "This Week" },
  { id: "this_month", label: "M", title: "This Month" },
  { id: "this_year", label: "Y", title: "This Year" }
];

export const FilterSection = ({
  search,
  setSearch,
  filters = {},
  setFilters = () => { },
  viewStyle,
  setViewStyle,
  onAddClick,
  addLabel = "Add",
  addVariant = "primary",
  showDetails,
  setShowDetails,
  onExport,
  isExporting = false,
  statusOptions = [],
  showAdvancedFilters,
  setShowAdvancedFilters,
  AdvancedFiltersComponent,
  filterOptions,
  statusPlaceholder = "All Status",
  customerOptions = [],
  customerPlaceholder = "All Vendors",
  statusFilterKey = 'status',
  hideFilterButton = false,
  hideViewToggle = false,
  children,
  customActions,
  resetButton
}) => {
  const [isSearchVisible, setIsSearchVisible] = useState(false);

  const handleFilterChange = (key, value) => {
    setFilters(key, value);
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Main Control Row */}
      <div className="py-2 flex flex-wrap items-center gap-3 bg-transparent w-full relative z-20">
        <div className="absolute top-0 right-0 w-64 h-64 bg-brand-blue/10 rounded-full blur-3xl pointer-events-none -z-10" />

        {/* Toggles */}
        {setSearch && (
          <FilterToggleButton
            active={isSearchVisible}
            onClick={() => {
              setIsSearchVisible(!isSearchVisible);
              if (isSearchVisible) setSearch("");
            }}
            icon={<Search size={18} />}
            title="Search"
          />
        )}

        {!hideFilterButton && setShowAdvancedFilters && AdvancedFiltersComponent && (
          <FilterToggleButton
            active={showAdvancedFilters}
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            icon={<Filter size={18} />}
            title="Filters"
            colorClass="bg-brand-blue"
          />
        )}

        {/* Quick Status Select */}
        {statusOptions.length > 0 && (
          <div className="bg-slate-50/50 p-1 rounded-md border border-slate-200/60 shrink-0">
            <select
              className="text-[13px] font-medium bg-white border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-blue text-slate-600 w-44"
              value={filters[statusFilterKey] || "all"}
              onChange={e => handleFilterChange(statusFilterKey, e.target.value)}
            >
              <option value="all">{statusPlaceholder}</option>
              {statusOptions.map((s, i) => {
                const label = typeof s.label === 'object' ? (s.label?.categoryName || s.label?.name || "—") : (s.label || "—");
                return (
                  <option key={s.value || i} value={s.value}>{String(label)}</option>
                );
              })}
            </select>
          </div>
        )}

        {/* Quick Customer Select */}
        {customerOptions.length > 0 && (
          <div className="bg-slate-50/50 p-1 rounded-md border border-slate-200/60 shrink-0">
            <select
              className="text-[13px] font-medium bg-white border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-blue text-slate-600 w-44"
              value={filters.customerName || "all"}
              onChange={e => handleFilterChange('customerName', e.target.value)}
            >
              <option value="all">{customerPlaceholder}</option>
              {customerOptions.map((name, i) => (
                <option key={name || i} value={name}>{name}</option>
              ))}
            </select>
          </div>
        )}

        {children}

        {/* Date Picker */}
        <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar py-1">
          <DateFilter value={filters.exactDate} onChange={val => handleFilterChange('exactDate', val)} label="Date" />

          {filters.exactDate && (
            <button
              type="button"
              onClick={() => handleFilterChange('exactDate', '')}
              className="p-2 rounded-lg text-rose-500 cursor-pointer hover:text-rose-600 hover:bg-rose-50 border border-rose-200 transition-all bg-white shadow-sm"
              title="Clear date filter"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Quick Date Presets */}
        <div className="flex items-center gap-1 bg-slate-50/50 p-1 rounded-lg border border-slate-100 shrink-0">
          {(QUICK_DATE_PRESETS || [
            { id: "today", label: "T", title: "Today" },
            { id: "this_week", label: "W", title: "This Week" },
            { id: "this_month", label: "M", title: "This Month" },
            { id: "this_year", label: "Y", title: "This Year" }
          ]).map(t => (
            <button
              key={t.id}
              type="button"
              onClick={() => handleFilterChange('date', filters.date === t.id ? 'all' : t.id)}
              className={`w-9 h-9 flex items-center cursor-pointer justify-center text-[13px] font-bold border transition-all rounded-md shrink-0 ${filters.date === t.id
                ? "bg-slate-900 text-white border-slate-900 shadow-md scale-105"
                : "bg-white text-slate-500 border-slate-200 hover:border-slate-400 hover:text-slate-950 shadow-sm"
                }`}
              title={t.title || t.label}
            >
              {t.label}
            </button>
          ))}
        </div>


        {resetButton}

        {/* Actions */}
        <div className="flex items-center gap-2 sm:ml-auto shrink-0">
          {setShowDetails && (
            <FilterToggleButton
              active={showDetails}
              onClick={() => setShowDetails(!showDetails)}
              icon={showDetails ? <EyeOff size={18} /> : <Eye size={18} />}
              title={showDetails ? "Hide stats" : "Show stats"}
              colorClass={showDetails ? "bg-slate-900" : "bg-slate-100"}
            />
          )}

          {viewStyle && setViewStyle && !hideViewToggle && (
            <ViewToggle viewStyle={viewStyle} setViewStyle={setViewStyle} />
          )}
          {/* Halimunnisa 09-06-2024 updated to hide view toggle for now as per discussion with team, can be enabled later when needed */}
          {customActions}

          {viewStyle && setViewStyle && hideViewToggle && (
            <div className="hidden">
              <ViewToggle viewStyle={viewStyle} setViewStyle={setViewStyle} />
            </div>
          )}

          {onExport && (
            <Button
              variant="secondary"
              size="sm"
              onClick={onExport}
              disabled={isExporting}
              className="h-9 hover:scale-105 transition-all rounded-md !font-normal text-slate-700"
              leftIcon={<Download size={18} />}
              title="Export"
            >
              <span className="hidden sm:inline">Export</span>
            </Button>
          )}

          {onAddClick && (
            <Button
              variant={addVariant}
              size="sm"
              onClick={onAddClick}
              className="h-9 hover:scale-105 shadow-sm cursor-pointer font-medium whitespace-nowrap rounded-md"
              leftIcon={<Plus size={18} />}
            >
              {addLabel}
            </Button>
          )}
        </div>
      </div>

      {/* Toggled Sections */}
      {isSearchVisible && setSearch && (
        <div className="px-3 pb-3 animate-in slide-in-from-top-1 duration-200">
          <SearchOverlay
            search={search}
            setSearch={setSearch}
            onClose={() => setIsSearchVisible(false)}
          />
        </div>
      )}

      {showAdvancedFilters && AdvancedFiltersComponent && (
        <div className="px-3 pb-3 animate-in slide-in-from-top-1 duration-200">
          <AdvancedFiltersComponent
            filters={filters}
            handleFilterChange={handleFilterChange}
            filterOptions={filterOptions}
          />
        </div>
      )}
    </div>
  );
};

export default FilterSection;




